import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  Application,
  FileType,
  FileUploadWithMetadata,
} from '@intellio/shared/models';
import {
  AppFileViewerService,
  ApplicationFilesService,
} from '@intellio/shared/services';
import { AccountFacade, ApplicationFacade } from '@intellio/shared/state-mgmt';
import {
  FileUploadValidationConfig,
  formatSupportedFileUploadExtensions,
  getFormRoot,
  isUnsupportedFileUpload,
  parseFileUploadValidationConfig,
} from '@intellio/shared/utils';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { Subject, take } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FileValueAccessorDirective } from './file-value-accessor.directive';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'itc-formly-field-file-type',
  template: `
    <div [id]="id" class="file-upload-field">
      <input
        type="file"
        hidden
        [formControl]="control"
        class="file-input"
        (change)="onFileSelected($event)"
        #fileUpload
        [required]="props.required"
        />
        <div class="file-upload-title">
          <strong>{{ props.title }}</strong>
          @if (props.required) {
            <span
          [ngClass]="{
            'required-asterisk': true,
            'ng-error': control.invalid && control.pristine === false
          }"
              >
              *
            </span>
          }
        </div>
        <mat-form-field class="notes-form-field">
          <mat-label>File Notes - add before uploading (optional)</mat-label>
          <input
            type="text"
            i18n-placeholder
            matInput
            [formControl]="notesControl"
            />
          </mat-form-field>
          @if (!isLoading) {
            <div class="file-upload">
              <button
                type="button"
                class="primary-button upload-button"
                (click)="fileUpload.click()"
                matTooltip="Upload a file"
          [disabled]="
            props.disabled ||
            (control.value !== null && control.value !== undefined)
          "
                >
                <mat-icon>upload</mat-icon>
              </button>
              <div>{{ fileNameControl.value || 'Select a file to upload' }}</div>
              @if (fileNameControl.value) {
                @if (props.previewable) {
                  <button
                    type="button"
                    mat-stroked-button
                    class="preview-button"
                    [disabled]="isPreviewLoading"
                    (click)="togglePreview()"
                  >
                    <mat-icon>{{ isPreviewOpen ? 'close' : 'visibility' }}</mat-icon>
                    @if (isPreviewOpen) {
                      <span i18n>Close Preview</span>
                    } @else if (isPreviewLoading) {
                      <span i18n>Loading Preview</span>
                    } @else {
                      <span i18n>Preview File</span>
                    }
                  </button>
                }
                <button
                  type="button"
                  mat-icon-button
                  (click)="deleteFile()"
                  matTooltip="Remove File"
                  >
                  <mat-icon class="remove-icon">close</mat-icon>
                </button>
              }
            </div>
          }
          @if (unsupportedFileTypeError) {
            <div class="file-upload-error">
              <mat-icon>warning</mat-icon>
              <span>
                {{ unsupportedFileTypeError }}.
                <ng-container i18n>Supported:</ng-container>
                {{ supportedFileExtensionsLabel }}
              </span>
            </div>
          }
          @if (previewError) {
            <div class="file-upload-error" role="alert">
              <mat-icon>warning</mat-icon>
              <span>{{ previewError }}</span>
            </div>
          }
          @if (isLoading) {
            <div class="in-place-spinner-wrapper">
              <mat-spinner></mat-spinner>
            </div>
          }
        </div>
    `,
  styles: [
    `
      .file-upload-error {
        align-items: center;
        color: #a94442;
        display: flex;
        gap: 8px;
        margin-top: 8px;
      }

      .file-upload-error mat-icon {
        font-size: 20px;
        height: 20px;
        width: 20px;
      }

      .preview-button {
        margin-left: auto;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatSpinner,
    MatIconModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
    FileValueAccessorDirective,
    MatButtonModule,
    MatTooltipModule,
  ],
})
export class FileTypeComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  private readonly destroyed$ = new Subject<void>();
  private previewObjectUrl?: string;
  private previewedFileName?: string;
  constructor(
    private appFilesService: ApplicationFilesService,
    private appFileViewerService: AppFileViewerService,
    private accountFacade: AccountFacade,
    private applicationFacade: ApplicationFacade,
    private cd: ChangeDetectorRef,
    private snackBarService: MatSnackBar
  ) {
    super();
  }

  control: UntypedFormControl;
  notesControl = new UntypedFormControl();
  fileNameControl = new UntypedFormControl();
  fileTypes: FileType[] = [];

  isLoading: boolean;
  uploaderId: string;
  uploaderName: string;
  userIsExternal: boolean;
  applicationId: string;
  currentApplication: Application;
  unsupportedFileTypeError: string | null;
  supportedFileExtensionsLabel: string | null;
  isPreviewLoading = false;
  previewError: string | null = null;

  ngOnInit() {
    this.isLoading = false;
    this.control = this?.formControl as UntypedFormControl;
    if (this.control.value != null) {
      this.field.options.formState.files$.subscribe((files) => {
        const pastUpload = files.find((f) => f.fileName === this.control.value);
        if (pastUpload) {
          this.notesControl.setValue(pastUpload.notes);
          this.notesControl.disable();
          this.fileNameControl.setValue(pastUpload.fileName);
          this.fileNameControl.disable();
        } else {
          this.control.setValue(null);
          this.cd.markForCheck();
        }
      });
    }
    this.accountFacade.currentUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.uploaderId = user.id;
        this.uploaderName = user.fullName;
        this.userIsExternal = user.isExternal;
      });
    this.applicationFacade.currentApplication$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((app) => {
        this.applicationId = app.id;
        this.currentApplication = app;
      });
    this.appFileViewerService.visibleChanges$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.cd.markForCheck());
  }

  get isPreviewOpen(): boolean {
    return (
      !!this.previewObjectUrl &&
      this.previewedFileName === this.fileNameControl.value &&
      this.appFileViewerService.isVisible &&
      this.appFileViewerService.viewer?.filePath === this.previewObjectUrl
    );
  }

  generateFileName(fileTypeId: string) {
    if (this.fileTypes.length > 0) {
      const fileType = this.fileTypes.filter((val) => {
        return val.id == fileTypeId;
      });
      return fileType[0].name;
    }
    return 'Other';
  }

  deleteFile() {
    this.closePreview();
    if (!this.control.value) {
      this.clearFileSelection();
      return;
    }

    this.appFilesService
      .deleteFile(this.applicationId, this.control.value, false)
      .subscribe(() => {
        this.clearFileSelection();
      });
  }

  uploadFile(event: Event) {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList) {
      console.log('FileUpload -> files', fileList);
    }
  }

  onFileSelected(event) {
    this.closePreview();
    const file = event.target.files[0];
    event.target.value = null;
    if (file) {
      this.isLoading = true;
      const metadata = new FileUploadWithMetadata();
      metadata.file = file;
      if (file.type !== '') {
        metadata.fileMimeType = file.type;
      } else {
        // The browser didn't recognize the file type.
        // However, we still require a MimeType, so we use a placeholder type.
        metadata.fileMimeType = 'unknown';
      }

      metadata.notes = this.notesControl.value ? this.notesControl.value : '';
      metadata.forInternalUseOnly = !this.userIsExternal;
      metadata.fileTypeId = this.props.fileType ? this.props.fileType : 'Other';

      metadata.fileName = file.name;
      metadata.uploaderId = this.uploaderId;
      metadata.uploaderFullName = this.uploaderName;
      metadata.fileUploadDate = new Date().toDateString();

      if (this.isUnsupportedFileType(metadata.fileName, metadata.fileTypeId)) {
        this.control.setValue(null);
        this.fileNameControl.setValue(file.name);
        this.setUnsupportedFileTypeError();
        this.isLoading = false;
        this.cd.markForCheck();
        return;
      }

      this.clearUnsupportedFileTypeError();
      this.appFilesService.uploadFile(this.applicationId, metadata).subscribe(
        (uploadedFile) => {
          this.control.setValue(uploadedFile.data.fileName);
          this.fileNameControl.setValue(uploadedFile.data.fileName);
          this.notesControl.disable();
          this.isLoading = false;
          this.cd.markForCheck();
        },
        () => {
          this.control.setValue(null);
          this.fileNameControl.setValue(null);
          this.cd.markForCheck();
          this.snackBarService.open('Error uploading file', 'Close', {
            duration: 10000,
            verticalPosition: 'bottom',
            horizontalPosition: 'center',
            panelClass: ['snackbar-warn'],
          });
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.closePreview();
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }

  togglePreview(): void {
    if (!this.props.previewable || !this.fileNameControl.value) {
      return;
    }

    if (this.isPreviewOpen) {
      this.closePreview();
      return;
    }

    const fileName = this.fileNameControl.value;
    this.previewError = null;
    this.isPreviewLoading = true;
    this.cd.markForCheck();

    this.appFileViewerService
      .getApplicationFilePreview(this.applicationId, fileName)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        next: (preview) => {
          if (this.fileNameControl.value !== fileName) {
            this.finishPreviewLoading();
            return;
          }

          this.revokePreviewObjectUrl();
          this.previewObjectUrl = URL.createObjectURL(preview);
          this.previewedFileName = fileName;
          if (this.appFileViewerService.isVisible) {
            this.appFileViewerService.hide();
          }
          this.appFileViewerService.setFile(this.previewObjectUrl);
          this.appFileViewerService.show();
          this.finishPreviewLoading();
        },
        error: () => {
          this.previewError = $localize`Unable to preview file.`;
          this.finishPreviewLoading();
        },
      });
  }

  private isUnsupportedFileType(fileName: string, fileTypeId: string): boolean {
    return isUnsupportedFileUpload(this.getFileUploadValidationConfig(), {
      appTypeId: this.getCurrentFormAppTypeId(),
      energySourceId: this.getCurrentFormEnergySourceId(),
      fileTypeId,
      fileName,
    });
  }

  private getFileUploadValidationConfig(): FileUploadValidationConfig {
    return parseFileUploadValidationConfig(
      this.field.options?.formState?.fileManagementModuleConfigs
    );
  }

  private setUnsupportedFileTypeError(): void {
    const config = this.getFileUploadValidationConfig();
    const errors = { ...(this.control.errors ?? {}) };

    this.unsupportedFileTypeError = $localize`Unsupported file format`;
    this.supportedFileExtensionsLabel =
      formatSupportedFileUploadExtensions(config);
    this.control.setErrors({ ...errors, unsupportedFileType: true });
    this.control.markAsTouched();
  }

  private clearUnsupportedFileTypeError(): void {
    this.unsupportedFileTypeError = null;
    this.supportedFileExtensionsLabel = null;

    if (!this.control?.errors?.unsupportedFileType) {
      return;
    }

    const errors = { ...(this.control.errors ?? {}) };
    delete errors.unsupportedFileType;
    this.control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private clearFileSelection(): void {
    this.notesControl.setValue(null);
    this.control.setValue(null);
    this.fileNameControl.setValue(null);
    this.notesControl.enable();
    this.control.enable();
    this.clearUnsupportedFileTypeError();
    this.cd.detectChanges();
  }

  private closePreview(): void {
    if (
      this.previewObjectUrl &&
      this.appFileViewerService.viewer?.filePath === this.previewObjectUrl &&
      this.appFileViewerService.isVisible
    ) {
      this.appFileViewerService.hide();
    }
    this.revokePreviewObjectUrl();
    this.previewedFileName = undefined;
    this.isPreviewLoading = false;
    this.previewError = null;
    this.cd.markForCheck();
  }

  private revokePreviewObjectUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = undefined;
    }
  }

  private finishPreviewLoading(): void {
    this.isPreviewLoading = false;
    this.cd.markForCheck();
  }

  private getCurrentFormAppTypeId(): string {
    const rootModel = this.getRootModel();

    return (
      this.extractOptionValue(rootModel?.application_type) ??
      this.currentApplication?.typeId
    );
  }

  private getCurrentFormEnergySourceId(): string {
    const rootModel = this.getRootModel();

    return (
      this.extractOptionValue(rootModel?.energySourceId) ??
      this.extractOptionValue(
        rootModel?.equipment_information?.energy_source
      ) ??
      this.extractOptionValue(rootModel?.energy_source) ??
      this.extractOptionValue(rootModel?.system?.energySourceId) ??
      this.currentApplication?.system?.energySourceId ??
      this.currentApplication?.energySourceId
    );
  }

  private getRootModel(): any {
    return getFormRoot(this.field)?.model;
  }

  private extractOptionValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object') {
      return value.value ?? value.id ?? value.key ?? null;
    }

    return value;
  }
}
